import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { styles } from '../styles';
import { SectionWrapper } from '../hoc';
import { fadeIn, textVariant, staggerContainer } from '../utils/motion';
import { github } from '../assets';

const GitHubContributions = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    contributions: 0,
    commits: 0,
    pullRequests: 0,
    codeReviews: 0,
    repositories: 0,
    languages: [],
    topLanguages: [],
    currentStreak: 0,
    longestStreak: 0,
    totalStars: 0,
    lastUpdated: '',
    message: ''
  });

  useEffect(() => {
    const fetchGitHubStats = async () => {
      try {
        const response = await fetch('/github-stats.json');

        if (!response.ok) {
          throw new Error('Failed to fetch GitHub stats');
        }

        const data = await response.json();
        setStats(data);
        setLoading(false);
      } catch (error) {
        setError(error.message);
        setLoading(false);
      }
    };

    fetchGitHubStats();
  }, []);

  const renderStats = () => {
    if (loading) {
      return <div className="text-white text-center">Loading contributions...</div>;
    }

    if (error) {
      return (
        <div className="text-center">
          <div className="text-red-500 mb-4">{error}</div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-[#1d1836] p-4 rounded-xl opacity-50">
              <p className="text-white text-lg font-semibold">-</p>
              <p className="text-white text-sm">Total Activities</p>
            </div>
            <div className="bg-[#1d1836] p-4 rounded-xl opacity-50">
              <p className="text-white text-lg font-semibold">-</p>
              <p className="text-white text-sm">Code Commits</p>
            </div>
            <div className="bg-[#1d1836] p-4 rounded-xl opacity-50">
              <p className="text-white text-lg font-semibold">-</p>
              <p className="text-white text-sm">Pull Requests</p>
            </div>
          </div>
          <p className="text-white text-sm mt-2">
            <span className="text-[#ff6b6b]">⚠️</span> Stats temporarily unavailable
          </p>
        </div>
      );
    }

    return (
      <div className="mt-4 text-center">
        <p className="text-white text-sm mb-4">
          <span className="text-[#ff6b6b]">🔥</span> {stats.message}
        </p>

        <motion.div
          variants={staggerContainer()}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          className="grid grid-cols-3 gap-4 mb-4"
        >
          <motion.div
            variants={fadeIn('right', 'spring', 0.2, 0.75)}
            whileHover={{
              scale: 1.05,
              backgroundColor: '#2d1f4a',
              transition: { duration: 0.2 }
            }}
            className="bg-[#1d1836] p-4 rounded-xl cursor-pointer"
          >
            <p className="text-white text-lg font-semibold">{stats.contributions}</p>
            <p className="text-white text-sm">Total Contributions</p>
          </motion.div>
          <motion.div
            variants={fadeIn('right', 'spring', 0.3, 0.75)}
            whileHover={{
              scale: 1.05,
              backgroundColor: '#2d1f4a',
              transition: { duration: 0.2 }
            }}
            className="bg-[#1d1836] p-4 rounded-xl cursor-pointer"
          >
            <p className="text-white text-lg font-semibold">{stats.commits}</p>
            <p className="text-white text-sm">Code Commits</p>
          </motion.div>
          <motion.div
            variants={fadeIn('right', 'spring', 0.4, 0.75)}
            whileHover={{
              scale: 1.05,
              backgroundColor: '#2d1f4a',
              transition: { duration: 0.2 }
            }}
            className="bg-[#1d1836] p-4 rounded-xl cursor-pointer"
          >
            <p className="text-white text-lg font-semibold">{stats.pullRequests}</p>
            <p className="text-white text-sm">Pull Requests</p>
          </motion.div>
        </motion.div>

        <motion.div
          variants={staggerContainer()}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          className="grid grid-cols-3 gap-4 mb-4"
        >
          <motion.div
            variants={fadeIn('right', 'spring', 0.5, 0.75)}
            whileHover={{
              scale: 1.05,
              backgroundColor: '#2d1f4a',
              transition: { duration: 0.2 }
            }}
            className="bg-[#1d1836] p-4 rounded-xl cursor-pointer"
          >
            <p className="text-white text-lg font-semibold">{stats.codeReviews}</p>
            <p className="text-white text-sm">Code Reviews</p>
          </motion.div>
          <motion.div
            variants={fadeIn('right', 'spring', 0.6, 0.75)}
            whileHover={{
              scale: 1.05,
              backgroundColor: '#2d1f4a',
              transition: { duration: 0.2 }
            }}
            className="bg-[#1d1836] p-4 rounded-xl cursor-pointer"
          >
            <p className="text-white text-lg font-semibold">{stats.currentStreak}</p>
            <p className="text-white text-sm">Current Streak</p>
          </motion.div>
          <motion.div
            variants={fadeIn('right', 'spring', 0.7, 0.75)}
            whileHover={{
              scale: 1.05,
              backgroundColor: '#2d1f4a',
              transition: { duration: 0.2 }
            }}
            className="bg-[#1d1836] p-4 rounded-xl cursor-pointer"
          >
            <p className="text-white text-lg font-semibold">{stats.longestStreak}</p>
            <p className="text-white text-sm">Longest Streak</p>
          </motion.div>
        </motion.div>

        <motion.div
          variants={staggerContainer()}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          className="grid grid-cols-3 gap-4 mb-4"
        >
          <motion.div
            variants={fadeIn('right', 'spring', 0.8, 0.75)}
            whileHover={{
              scale: 1.05,
              backgroundColor: '#2d1f4a',
              transition: { duration: 0.2 }
            }}
            className="bg-[#1d1836] p-4 rounded-xl cursor-pointer"
          >
            <p className="text-white text-lg font-semibold">{stats.repositories}</p>
            <p className="text-white text-sm">Total Repositories</p>
          </motion.div>
          <motion.div
            variants={fadeIn('right', 'spring', 0.9, 0.75)}
            whileHover={{
              scale: 1.05,
              backgroundColor: '#2d1f4a',
              transition: { duration: 0.2 }
            }}
            className="bg-[#1d1836] p-4 rounded-xl cursor-pointer"
          >
            <p className="text-white text-lg font-semibold">{stats.languages.length}</p>
            <p className="text-white text-sm">Languages</p>
          </motion.div>
          <motion.div
            variants={fadeIn('right', 'spring', 1, 0.75)}
            whileHover={{
              scale: 1.05,
              backgroundColor: '#2d1f4a',
              transition: { duration: 0.2 }
            }}
            className="bg-[#1d1836] p-4 rounded-xl cursor-pointer"
          >
            <p className="text-white text-lg font-semibold">{stats.totalStars}</p>
            <p className="text-white text-sm">Total Stars</p>
          </motion.div>
        </motion.div>

        {stats.topLanguages.length > 0 && (
          <motion.div
            variants={fadeIn('up', 'spring', 1.1, 0.75)}
            initial={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.25 }}
            className="mt-4"
          >
            <h4 className="text-white text-lg font-semibold mb-2">Top Languages</h4>
            <div className="flex flex-wrap justify-center gap-2">
              {stats.topLanguages.map((lang, index) => (
                <motion.div
                  key={index}
                  variants={fadeIn('up', 'spring', 1.2 + index * 0.1, 0.75)}
                  initial={{ opacity: 1 }}
                  whileHover={{
                    scale: 1.1,
                    backgroundColor: '#2d1f4a',
                    transition: { duration: 0.2 }
                  }}
                  className="bg-[#1d1836] px-3 py-1 rounded-full cursor-pointer"
                >
                  <span className="text-white text-sm">{lang.name}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        <motion.p
          variants={fadeIn('up', 'spring', 1.3, 0.75)}
          initial={{ opacity: 1 }}
          className="text-white text-sm mt-4"
        >
          <span className="text-[#ff6b6b]">✨</span> Many more contributions in private repositories
        </motion.p>
      </div>
    );
  };

  return (
    <>
      <motion.div variants={textVariant()}>
        <p className={styles.sectionSubText}>My Activity</p>
        <h2 className={styles.sectionHeadText}>GitHub Contributions</h2>
      </motion.div>

      <motion.div
        variants={fadeIn('', '', 0.1, 1)}
        className="mt-4 flex flex-col items-center">
        <div className="w-full bg-tertiary p-8 rounded-2xl relative">
          <div className="flex items-center justify-between mb-6">
            <img src={github} alt="github" className="w-12 h-12" />
            <h3 className="text-white text-[24px] font-bold">GitHub Activity</h3>
          </div>

          <div className="relative z-10">
            {renderStats()}
          </div>

          <div className="flex justify-end mt-8 relative z-10">
            <motion.a
              href="https://github.com/ersachinsaurav"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#915eff] text-sm hover:text-[#ff6b6b] transition-all duration-300 hover:scale-105 cursor-pointer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="flex items-center gap-2">
                Visit my GitHub profile
                <motion.span
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  →
                </motion.span>
              </span>
            </motion.a>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default SectionWrapper(GitHubContributions, 'github');
